import { CommandCenterHeader } from "@components";
import { Feature, Role } from "@enums";
import { Card, Stack } from "@mui/material";
import {
  useLazyWebApiV1SearchOrdersQuery,
  useWebApiV1GetUserFeatureSwitchesQuery,
  useWebApiV1GetUserPreferencesQuery,
} from "@providers/hop-ord-server/api";
import { useCheckUserRole } from "@utils";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
  CommandCenterTable,
  noSearchTextToolTips,
  searchTextToolTips,
} from "./CommandCenterComponents";
import SearchAndFilterBar from "./SearchAndFilterBar";

const CommandCenter = () => {
  const { data: featureSwitchData } = useWebApiV1GetUserFeatureSwitchesQuery();
  const { data: userPreferenceData } = useWebApiV1GetUserPreferencesQuery();

  const tenantIds = userPreferenceData?.tenant_ids;
  const isRo = useCheckUserRole(Role.RO);

  // RO default statuses when no user preference statuses are provided
  const roDefaultStatuses: string[] = [
    "Pending plan review",
    "Approved",
    "Rejected",
  ];

  // All the good stuff for the order search
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>("");

  const [selectedStatus, setSelectedStatus] = useState<string[]>(
    userPreferenceData?.statuses || [],
  );
  const [selectedSite, setSelectedSite] = useState<string[]>(
    userPreferenceData?.treating_departments || [],
  );
  const [selectedPhysician, setSelectedPhysician] = useState<string[]>(
    userPreferenceData?.physicians || [],
  );
  const [selectedQuickFilters, setSelectedQuickFilters] = useState<string[]>(
    userPreferenceData?.quick_filters || [],
  );

  const [trigger, { data, isLoading }] = useLazyWebApiV1SearchOrdersQuery({
    pollingInterval: 30_000,
  });

  useEffect(() => {
    trigger({
      searchText: debouncedSearchText,
      siteFilter: selectedSite.join(","),
      physicianFilter: selectedPhysician.join(","),
      statusFilter: selectedStatus.join(","),
      quickFilter: selectedQuickFilters.join(","),
      ordering: "-submitted_datetime",
    });
  }, [
    debouncedSearchText,
    selectedSite,
    selectedPhysician,
    selectedStatus,
    selectedQuickFilters,
  ]);

  const [sortBy, setSortBy] = useState<string>("rxSubmitted");
  const [sortDirection, setSortDirection] = useState<string>("desc");

  const showMockControls =
    featureSwitchData?.find(
      (feature) => feature.name === Feature.MOCK_CONTROLS,
    ) ?? false;

  useEffect(() => {
    if (userPreferenceData) {
      // Only override statuses if the preference contains a value; otherwise apply RO defaults when applicable
      if (userPreferenceData?.statuses?.length) {
        setSelectedStatus(userPreferenceData.statuses || []);
      } else if (isRo) {
        setSelectedStatus(roDefaultStatuses);
      } else {
        setSelectedStatus([]);
      }
      setSelectedSite(userPreferenceData?.treating_departments || []);
      setSelectedPhysician(userPreferenceData?.physicians || []);
      setSelectedQuickFilters(userPreferenceData?.quick_filters || []);
    }
  }, [userPreferenceData, isRo]);

  // Addded debounce to the search text to prevent lots of spam
  const debouncedSetSearchText = useCallback(
    debounce(setDebouncedSearchText, 150),
    [],
  );
  useEffect(() => {
    debouncedSetSearchText(searchText);
  }, [searchText]);

  const handleSortClick = (newSortBy: string) => {
    if (sortBy === newSortBy) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(newSortBy);
    }
  };

  const noResultsMessage = searchText ? ` for "${searchText}"` : "";
  const toolTips = searchText ? searchTextToolTips : noSearchTextToolTips;

  return (
    <Stack
      gap={3}
      padding={2}
      height={1}
      maxHeight={1}
      width={1}
      maxWidth="1700px"
      minWidth={isLoading || !data?.length ? "1700px" : "default"}
      marginLeft="auto"
      marginRight="auto"
    >
      <CommandCenterHeader title="Command Center" />
      <SearchAndFilterBar
        setSearchText={setSearchText}
        searchText={searchText}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedSite={selectedSite}
        setSelectedSite={setSelectedSite}
        selectedPhysician={selectedPhysician}
        setSelectedPhysician={setSelectedPhysician}
        selectedQuickFilters={selectedQuickFilters}
        setSelectedQuickFilters={setSelectedQuickFilters}
        tenantIds={tenantIds ?? []}
      />
      <Card>
        <CommandCenterTable
          data={data}
          isLoading={isLoading}
          sortBy={sortBy}
          sortDirection={sortDirection}
          handleSortClick={handleSortClick}
          noResultsMessage={noResultsMessage}
          toolTips={toolTips}
          showMockControls={showMockControls}
          isRo={isRo}
        />
      </Card>
    </Stack>
  );
};

export default CommandCenter;
